/**
 * Word search for in-app lists (one rule app-wide, like menu directory search).
 *
 * Every typed word must appear as real text in the item's fields — words may
 * land in different fields ("indoor gelato" finds an indoor Gelato request).
 * Scattered letters across a sentence never match: typing "gelato" must not
 * return "lookinG somE fLower thAT pOunds".
 *
 * Ranking: whole query as one phrase beats separate words; earlier beats later.
 */

const normalize = (s: string) => s.toLocaleLowerCase().trim();
const wordsOf = (q: string) => normalize(q).split(/\s+/).filter(Boolean);

function scoreText(h: string, query: string): number {
  const q = normalize(query);
  if (!q) return 1;
  if (!h) return 0;
  const words = wordsOf(q);
  if (!words.every((w) => h.includes(w))) return 0;
  const idx = h.indexOf(q);
  if (idx >= 0) {
    const positionBonus = 1 - Math.min(idx, 20) / 40; // 1 .. 0.5
    return 0.7 + 0.2 * positionBonus + 0.1 * (q.length / h.length);
  }
  return 0.5;
}

/** Score one field. Returns 0 when it isn't a match. */
export function fuzzyScore(haystack: string | null | undefined, query: string): number {
  return scoreText(normalize(haystack || ''), query);
}

/** Score several fields together; each word may match a different field. */
export function fuzzyScoreFields(fields: Array<string | null | undefined>, query: string): number {
  const parts = fields.filter((f): f is string => !!f).map(normalize);
  let best = 0;
  for (const p of parts) best = Math.max(best, scoreText(p, query));
  if (best > 0) return best;
  return scoreText(parts.join(' \u0000 '), query) > 0 ? 0.4 : 0;
}

/** Filter + rank a list. With an empty query the original order is preserved. */
export function fuzzyFilter<T>(
  items: T[],
  query: string,
  getFields: (item: T) => Array<string | null | undefined>,
): T[] {
  if (!normalize(query)) return items;
  return items
    .map((item) => ({ item, score: fuzzyScoreFields(getFields(item), query) }))
    .filter((r) => r.score > 0)
    .sort((a, b) => b.score - a.score)
    .map((r) => r.item);
}
